import { Component, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { SlicePipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faImage } from '@fortawesome/pro-duotone-svg-icons/faImage';
import { faXmark } from '@fortawesome/pro-duotone-svg-icons/faXmark';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { FileDropZone } from './file-drop-zone/file-drop-zone';
import { IconBtn } from '../ui/buttons/icon-btn/icon-btn';
import { ImageUploadItem } from './models';
import {
  ALLOWED_UPLOAD_IMAGE_TYPES,
  fitImageToUploadLimit,
  formatBytes,
  ImageTooLargeError,
  MAX_UPLOAD_IMAGE_BYTES,
} from '../../helpers/upload-image';

/**
 * Presentational drop zone for a single image. It owns nothing but the
 * selection UX: MIME check, downscaling to the upload limit, and local error
 * display. What it shows comes in via `item`; what the user picked goes out as
 * a plain `File` via `fileSelected`.
 */
@Component({
  selector: 'andor-image-upload',
  templateUrl: './image-upload.html',
  styleUrl: './image-upload.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FileDropZone, SlicePipe, IconBtn, FaIconComponent, TranslatePipe, MatProgressSpinner],
})
export class ImageUpload {
  description = input('Drag and drop your image here or click to select a file.');
  noFileSelectedText = input('No File selected');
  item = input<ImageUploadItem | undefined>(undefined);
  maxBytes = input(MAX_UPLOAD_IMAGE_BYTES);

  fileSelected = output<File>();
  removed = output<void>();

  protected readonly error = signal<string | null>(null);
  protected readonly processing = signal(false);
  protected readonly faImage = faImage;
  protected readonly faXmark = faXmark;
  protected readonly formatBytes = formatBytes;

  private readonly translate = inject(TranslateService);

  onFilesSelected(files: File[]) {
    void this.selectFrom(files);
  }

  removeItem(event: Event) {
    event.stopPropagation();
    this.error.set(null);
    this.removed.emit();
  }

  private async selectFrom(files: File[]) {
    this.error.set(null);
    const file = files[0];
    if (!file) {
      return;
    }
    if (!ALLOWED_UPLOAD_IMAGE_TYPES.includes(file.type)) {
      this.error.set(
        this.translate.instant('Unsupported file type. Allowed: PNG, JPEG, SVG, WebP.')
      );
      return;
    }

    this.processing.set(true);
    try {
      const fitted = await fitImageToUploadLimit(file, this.maxBytes());
      this.fileSelected.emit(fitted);
    } catch (e) {
      const size = e instanceof ImageTooLargeError ? e.size : file.size;
      this.error.set(
        this.translate.instant('Image is too large ([size]). Maximum size is [max].', {
          size: formatBytes(size),
          max: formatBytes(this.maxBytes(), 0),
        })
      );
    } finally {
      this.processing.set(false);
    }
  }
}
